import React from 'react';
import PropTypes from 'prop-types';
import LayoutPlaceholderCard from '../../../../components/shared/LayoutPlaceholderCard';
import { getCardPlaceholderMeta } from '../../../../utils/getCardDisplayTitle';
import { getHospitalLayoutSkeletonVariant } from '../../../../utils/hospitalLayoutPlaceholderSkeleton';
import { getLayoutPreviewSharePanelProps } from '../../../../utils/layoutPreviewShareBadge';
import {
	arePreviewPropsEqual,
	DaysPreviewBody,
	FloorsPreviewBody,
	InsurancePreviewBody,
	NinetyDaysPreviewBody,
	resolveDynamicPreviewBody,
	UserListPreviewBody,
} from '../../../../components/shared/layoutPreview';

/**
 * Layout-mode preview renderer for the Deceased page.
 *
 * See `components/shared/layoutPreview/` for the shared primitives. This
 * file just maps each Deceased-specific `card.component` value to the
 * matching shared body, or delegates to the dynamic resolver.
 */
const PREVIEW_BY_COMPONENT = {
	insurance: InsurancePreviewBody,
	doctor: UserListPreviewBody,
	days: DaysPreviewBody,
	floors: FloorsPreviewBody,
	ninetyDays: NinetyDaysPreviewBody,
};

const DeceasedCardLayoutPreview = ({ card, dynamicCards, sharedUsersByTabId }) => {
	const { title: placeholderTitle } = getCardPlaceholderMeta(card, {
		dynamicCards,
	});

	let PreviewBody = null;
	let previewBodyProps = {};
	if (card?.component === 'dynamic') {
		PreviewBody = resolveDynamicPreviewBody(card?.data);
		previewBodyProps = { role: 'generic', useGenericLabels: true };
	} else if (card?.component) {
		PreviewBody = PREVIEW_BY_COMPONENT[card.component] || null;
		if (card.component === 'doctor') {
			previewBodyProps = { role: 'doctor' };
		}
	}

	return (
		<LayoutPlaceholderCard
			variant="card"
			title={placeholderTitle}
			width={card?.width}
			sharePanel={getLayoutPreviewSharePanelProps(card, { sharedUsersByTabId })}
			skeletonVariant={getHospitalLayoutSkeletonVariant(card)}
			body={PreviewBody ? <PreviewBody {...previewBodyProps} /> : null}
		/>
	);
};

DeceasedCardLayoutPreview.propTypes = {
	card: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		component: PropTypes.string,
		type: PropTypes.string,
		width: PropTypes.number,
		data: PropTypes.object,
	}).isRequired,
	dynamicCards: PropTypes.array,
	sharedUsersByTabId: PropTypes.object,
};

DeceasedCardLayoutPreview.defaultProps = {
	dynamicCards: [],
	sharedUsersByTabId: undefined,
};

export default React.memo(DeceasedCardLayoutPreview, arePreviewPropsEqual);
